// email.ts — Patch MCC-5a. Email-infrastructure client: suppression list
// (do-not-email), send/address health counters, unsubscribe-token mint, and
// the cursor-batched profile_email_addresses backfill.

import { request } from './request'

export interface SendHealthWarmup {
  cap: number
  sent_today: number
  remaining: number
  bounce_rate: number
  bounced: number
  send_denominator: number
}

export interface EmailHealth {
  ok: boolean
  suppressions: { total: number; by_reason: Record<string, number> }
  send_records: { total: number; by_status: Record<string, number> }
  addresses: { total: number; by_validation_status: Record<string, number> }
  warmup: SendHealthWarmup
}

export interface ValidateResult {
  ok: boolean
  processed: number
  graded: { valid: number; risky: number; invalid: number }
  dns_errors: number
  unique_domains_checked: number
  doh_calls: number
  stopped_early: boolean
  next_after_id: string | null
  done: boolean
}

export interface SuppressionRow {
  id: string
  email: string
  reason: string
  source: string | null
  notes: string | null
  created_at: string
}

export interface SuppressionsPage {
  ok: boolean
  total: number
  limit: number
  offset: number
  suppressions: SuppressionRow[]
}

export interface BackfillResult {
  ok: boolean
  processed_profiles: number
  inserted: number
  already_present: number
  skipped_invalid: number
  next_after_id: string | null
  done: boolean
}

export const emailApi = {
  health: () => request<EmailHealth>('/api/admin/email/health'),

  listSuppressions: (opts?: { search?: string; limit?: number; offset?: number }) => {
    const params = new URLSearchParams()
    if (opts?.search) params.set('search', opts.search)
    if (opts?.limit) params.set('limit', String(opts.limit))
    if (opts?.offset) params.set('offset', String(opts.offset))
    const qs = params.toString()
    return request<SuppressionsPage>(`/api/admin/email/suppressions${qs ? `?${qs}` : ''}`)
  },

  addSuppression: (email: string, notes?: string) =>
    request<{ ok: boolean; id: string; created: boolean }>('/api/admin/email/suppressions', {
      method: 'POST',
      body: JSON.stringify({ email, notes }),
    }),

  removeSuppression: (id: string) =>
    request<{ ok: boolean; removed: boolean }>(`/api/admin/email/suppressions/${id}`, {
      method: 'DELETE',
    }),

  mintUnsubscribeToken: (email: string, profileId?: string) =>
    request<{ ok: boolean; token: string; path: string }>(
      '/api/admin/email/unsubscribe-tokens',
      { method: 'POST', body: JSON.stringify({ email, profile_id: profileId }) },
    ),

  addressesBackfill: (afterId?: string | null, batchSize?: number) =>
    request<BackfillResult>('/api/admin/email/addresses-backfill', {
      method: 'POST',
      body: JSON.stringify({ after_id: afterId ?? undefined, batch_size: batchSize }),
    }),

  setCap: (value: number) =>
    request<{ ok: boolean; cap: number }>('/api/admin/email/cap', {
      method: 'PUT',
      body: JSON.stringify({ value }),
    }),

  validateAddresses: (afterId?: string | null, batchSize?: number) =>
    request<ValidateResult>('/api/admin/email/validate-addresses', {
      method: 'POST',
      body: JSON.stringify({ after_id: afterId ?? undefined, batch_size: batchSize }),
    }),
}
